<template>
  <div
    class="widget price-table styles pt-8 md:pt-6"
    :class="{ 'widget--wide': columns.length > 2 }"
  >
    <LoadingIndicator v-if="!isReady" />

    <div v-else v-for="(_row, rowIndex) in dummyRows" class="row w-full">
      <TitleRow v-if="isShown('TitleRow', rowIndex)" :row="getRow(rowIndex)" />

      <AddonRow
        v-else-if="isShown('AddonRow', rowIndex)"
        :row="getRow(rowIndex)"
      />

      <ActionRow
        v-else-if="isShown('ActionRow', rowIndex)"
        :row="getRow(rowIndex)"
        :environment="environment"
        :hasSession="hasSession"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import VueI18n from 'vue-i18n'
import { init } from './core/bootstrap'
import { init as initI18n, updateTranslations } from './core/i18n'
import LoadingIndicator from '@/components/LoadingIndicator'
import TitleTagRow from './components/TitleTagRow'
import TitleRow from './components/TitleRow'
import ActionRow from './components/ActionRow'
import AddonRow from './components/AddonRow'
import WebFont from 'webfontloader'
import { isProduction } from './utils/core'

Vue.use(VueI18n)

const i18n = initI18n()

if (!isProduction) {
  WebFont.load({
    typekit: {
      id: 'bns5okr',
    },
  })
}

export default {
  name: 'PriceTable',
  components: {
    LoadingIndicator,
    TitleTagRow,
    TitleRow,
    ActionRow,
    AddonRow,
  },
  i18n,
  props: ['locale', 'environment', 'isAbsolutePath'],
  data() {
    return {
      isReady: false,
      hasSession: false,
      columns: [],
      rowKeys: [],
    }
  },
  computed: {
    dummyRows() {
      return new Array(this.totalRows)
    },
    totalRows() {
      // total number of addOn rows + (titleRow & actionRow):
      return this.rowKeys.length + 2
    },
    titleRow() {
      return [
        '',
        ...this.columns.map(el => ({ badge: el.titleTag, title: el.title })),
      ]
    },
    actionRow() {
      return [
        null,
        ...this.columns.map(
          ({ planCode, baseOneTimePrice, baseMonthlyPrice }) => ({
            planCode,
            baseOneTimePrice,
            baseMonthlyPrice,
          })
        ),
      ]
    },
    addonRows() {
      return this.rowKeys.map(key => {
        const columns = this.columns.map(el => el[key])
        const _key = `widget.priceTable.${key}`
        return [_key, ...columns]
      })
    },
  },
  created() {
    this.onCreated()
  },
  methods: {
    async load() {
      i18n.locale = this.locale
      const { status, payload } = await init(this.$props)
      if (status === 'success') {
        const { translations, priceTable } = payload
        updateTranslations(i18n, translations)
        this.columns = priceTable.columns
        this.rowKeys = priceTable.rowKeys
        this.hasSession = priceTable.hasSession
        this.isReady = true
      }
    },
    subscribeEventBus() {
      window.addEventListener('update', event => {
        this.load()
      })
    },
    async onCreated() {
      this.subscribeEventBus()
      this.load()
    },
    isShown(widget, index) {
      const lastRow = this.totalRows - 1

      return {
        TitleRow() {
          return index === 0
        },
        AddonRow() {
          return index > 0 && index < lastRow
        },
        ActionRow() {
          return index === lastRow
        },
      }[widget]()
    },
    getRow(index) {
      return [this.titleRow, ...this.addonRows, this.actionRow][index]
    },
  },
}
</script>

<style src="./tailwind.css" scoped></style>
<style lang="scss" src="./styles/core.scss"></style>

<style scoped lang="scss">
.widget {
  min-width: 322px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;

  &--wide {
    min-width: 514px;
  }
}
</style>
